import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Visibility,
  ExpandMore,
  DragIndicator,
} from '@mui/icons-material';

import { maintenanceService } from '../../services/maintenanceService';
import { useSiteContext } from '../../hooks/useSiteContext';
import {
  MaintenanceRoute,
  CreateMaintenanceRoute,
  MaintenanceRouteFilters,
  PaginatedResponse,
  RouteType,
} from '../../types/maintenance';

const MaintenanceRoutes: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [routes, setRoutes] = useState<MaintenanceRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<MaintenanceRoute | null>(null);
  const [routeForm, setRouteForm] = useState<Partial<CreateMaintenanceRoute>>({
    route_name: '',
    route_description: '',
    estimated_duration_hours: 0,
    site: 0,
    route_type: RouteType.GENERAL_MAINTENANCE,
  });
  
  // Separate state for route items (managed independently)
  const [routeItems, setRouteItems] = useState<any[]>([]);

  // Filtering
  const [filters, setFilters] = useState<MaintenanceRouteFilters>({});

  // Load routes
  const loadRoutes = useCallback(async () => {
    if (!currentSite.siteId) return;

    try {
      setLoading(true);
      setError(null);

      const searchFilters: MaintenanceRouteFilters = {
        ...filters,
        site: currentSite.siteId?.toString(),
        page: page + 1,
        page_size: rowsPerPage,
      };

      const response: PaginatedResponse<MaintenanceRoute> = 
        await maintenanceService.routes.getRoutes(searchFilters);

      setRoutes(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      console.error('Failed to load routes:', err);
      setError('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [currentSite.siteId, page, rowsPerPage, filters]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const handleCreateRoute = async () => {
    try {
      await maintenanceService.routes.createRoute(routeForm as CreateMaintenanceRoute);
      setCreateDialogOpen(false);
      resetForm();
      loadRoutes();
    } catch (err) {
      console.error('Failed to create route:', err);
    }
  };

  const handleEditRoute = async () => {
    if (!selectedRoute) return;

    try {
      await maintenanceService.routes.updateRoute(selectedRoute.id, routeForm);
      setEditDialogOpen(false);
      resetForm();
      loadRoutes();
    } catch (err) {
      console.error('Failed to update route:', err);
    }
  };

  const handleDeleteRoute = async (id: number) => {
    try {
      await maintenanceService.routes.deleteRoute(id);
      loadRoutes();
    } catch (err) {
      console.error('Failed to delete route:', err);
    }
  };

  const resetForm = () => {
    setRouteForm({
      route_name: '',
      route_description: '',
      estimated_duration_hours: 0,
      site: currentSite.siteId || 0,
      route_type: RouteType.GENERAL_MAINTENANCE,
    });
    setRouteItems([]);
    setSelectedRoute(null);
  };

  const openEditDialog = (route: MaintenanceRoute) => {
    setSelectedRoute(route);
    setRouteForm({
      route_name: route.route_name,
      route_description: route.route_description,
      estimated_duration_hours: route.estimated_duration_hours,
      route_type: route.route_type,
      site: route.site,
    });
    setRouteItems(route.route_items || []);
    setEditDialogOpen(true);
  };

  const addRouteItem = () => {
    const newItem = {
      equipment: 0,
      sequence_order: (routeItems.length || 0) + 1,
      estimated_time_minutes: 30,
      specific_instructions: '',
      inspection_points: [],
      critical_measurements: [],
      acceptable_ranges: {},
    };
    setRouteItems([...routeItems, newItem]);
  };

  const removeRouteItem = (index: number) => {
    const newItems = [...routeItems];
    newItems.splice(index, 1);
    // Reorder sequence
    newItems.forEach((item, idx) => {
      item.sequence_order = idx + 1;
    });
    setRouteItems(newItems);
  };

  const updateRouteItem = (index: number, field: string, value: any) => {
    const newItems = [...routeItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setRouteItems(newItems);
  };

  if (!currentSite.siteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a site to view maintenance routes.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Maintenance Routes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Route
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search routes..."
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Routes Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Route Name</TableCell>
                    <TableCell>Equipment Count</TableCell>
                    <TableCell>Estimated Duration</TableCell>
                    <TableCell>Last Executed</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routes.map((route) => (
                    <TableRow key={route.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {route.route_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {route.route_description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${route.route_items?.length || 0} equipment`}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {route.estimated_duration_hours} hours
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Not tracked
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="Execute Route">
                            <IconButton
                              size="small"
                              color="primary"
                              // onClick={() => handleExecuteRoute(route.id)}
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Route">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(route)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Route">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRoute(route.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {routes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No routes found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Create Route Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Create New Maintenance Route</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Route Name"
                value={routeForm.route_name}
                onChange={(e) => setRouteForm({ ...routeForm, route_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={routeForm.route_description}
                onChange={(e) => setRouteForm({ ...routeForm, route_description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Total Duration (hours)"
                value={routeForm.estimated_duration_hours}
                onChange={(e) => setRouteForm({ 
                  ...routeForm, 
                  estimated_duration_hours: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Route Items */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Route Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addRouteItem}
                >
                  Add Equipment
                </Button>
              </Box>

              {routeItems?.map((item, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <DragIndicator sx={{ mr: 1 }} />
                      <Typography>
                        Step {item.sequence_order}: Equipment {item.equipment || 'Not selected'}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Equipment ID"
                          value={item.equipment || ''}
                          onChange={(e) => updateRouteItem(index, 'equipment', parseInt(e.target.value) || 0)}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Estimated Time (minutes)"
                          value={item.estimated_time_minutes || ''}
                          onChange={(e) => updateRouteItem(index, 'estimated_time_minutes', parseInt(e.target.value) || 0)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Instructions"
                          value={item.specific_instructions || ''}
                          onChange={(e) => updateRouteItem(index, 'specific_instructions', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => removeRouteItem(index)}
                        >
                          Remove Item
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              {(!routeItems || routeItems.length === 0) && (
                <Alert severity="info">
                  No equipment added to this route. Click "Add Equipment" to start building your route.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateRoute} variant="contained">Create Route</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Route Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Maintenance Route</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Route Name"
                value={routeForm.route_name || ''}
                onChange={(e) => setRouteForm({ 
                  ...routeForm, 
                  route_name: e.target.value
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Route Description"
                value={routeForm.route_description || ''}
                onChange={(e) => setRouteForm({ 
                  ...routeForm, 
                  route_description: e.target.value
                })}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Duration (hours)"
                value={routeForm.estimated_duration_hours || ''}
                onChange={(e) => setRouteForm({ 
                  ...routeForm, 
                  estimated_duration_hours: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Route Items */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Route Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addRouteItem}
                >
                  Add Equipment
                </Button>
              </Box>

              {routeItems?.map((item, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <DragIndicator sx={{ mr: 1 }} />
                      <Typography>
                        Step {item.sequence_order}: Equipment {item.equipment || 'Not selected'}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Equipment ID"
                          value={item.equipment || ''}
                          onChange={(e) => updateRouteItem(index, 'equipment', parseInt(e.target.value) || 0)}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Estimated Time (minutes)"
                          value={item.estimated_time_minutes || ''}
                          onChange={(e) => updateRouteItem(index, 'estimated_time_minutes', parseInt(e.target.value) || 0)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Instructions"
                          value={item.specific_instructions || ''}
                          onChange={(e) => updateRouteItem(index, 'specific_instructions', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => removeRouteItem(index)}
                        >
                          Remove Item
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              {(!routeItems || routeItems.length === 0) && (
                <Alert severity="info">
                  No equipment added to this route. Click "Add Equipment" to start building your route.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditRoute} variant="contained">Update Route</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceRoutes; 